import React from "react"
import { Box } from "@mui/material"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import KpiTile from "@/components/ui/KpiTile"
import { CardHero, CourseTile, DetailsToggle, INFO_CARD_BODY_SX, finite, tileRow } from "@/components/map/InfoCardParts"
import { ETA_TILE_FORMAT } from "@/features/route/routeKpis"
import RouteHelper from "@/utils/routeHelper"
import { WW_BLUE } from "@/theme/tokens"

dayjs.extend(utc)

const fixed1 = (v) => (v == null ? null : v.toFixed(1))

export default function VesselCardContent({ selectedVessel, color = WW_BLUE }) {
  const features = selectedVessel?.geojson?.features ?? []
  const feature = features[0] ?? {}
  const props = feature.properties ?? {}
  const report = props.albis_reports ?? {}
  const sensor = props.albis_sensors ?? {}
  const analyse = props.analyse ?? {}

  const isReported = selectedVessel?.routeType === "reported"
  // Planned vessels have no heading sensor; a last-position marker has no course.
  const showHeading = !RouteHelper.isPlanType(selectedVessel?.routeType)
  const sog = finite(props.sog ?? sensor.speedOverGround_kn)
  const heading = finite(props.heading ?? sensor.headingTrue_deg)
  const cog = finite(isReported ? analyse.course_bearing : analyse.rhumbline_bearing)

  const positionTs = props.timestamp ?? props.measuredAt ?? null
  const latest = features.at(-1)?.properties
  const latestTs = latest?.timestamp ?? latest?.measuredAt ?? null
  const footnote =
    latestTs && positionTs && !dayjs.utc(latestTs).isSame(dayjs.utc(positionTs))
      ? `Last update ${dayjs.utc(latestTs).format("DD MMM HH:mm")} UTC`
      : null

  const etaTime = report.plannedETA_local_ISO ? dayjs.utc(report.plannedETA_local_ISO) : null
  // ALBIS sends 2000-01-01 as "no ETA".
  const eta = etaTime?.isValid() && etaTime.year() > 2000 ? etaTime.format(ETA_TILE_FORMAT) : null
  const draftAft = finite(report.draftAft_m)
  const draftFwd = finite(report.draftForward_m)
  const centerDraft = draftAft != null && draftFwd != null ? (draftAft + draftFwd) / 2 : null

  const motionCount = [sog, cog, showHeading ? heading : null].filter((v) => v != null).length
  // Reports and some sensors are not delivered for every vessel: only filled tiles are shown, as on the waypoint card.
  const voyageTiles = [
    { label: "Next port", value: report.nextPort_LOCODE || null },
    { label: "ETA (UTC)", value: eta },
  ].filter((t) => t.value != null)
  const detailTiles = [
    { label: "Center draft", value: fixed1(centerDraft), unit: "m" },
    { label: "Sailing type", value: report.typeOfSailing || null },
    { label: "M/E FCR", value: fixed1(finite(sensor.mainEngineFuelConsumptionRate_t_per_d)), unit: "t/d" },
    { label: "A/E FCR", value: fixed1(finite(sensor.auxEngineFuelConsumptionRate_t_per_d)), unit: "t/d" },
  ].filter((t) => t.value != null)

  return (
    <Box sx={INFO_CARD_BODY_SX}>
      <CardHero
        label={isReported ? "Reported" : "Estimated"}
        timestamp={positionTs ?? latestTs}
        coords={feature.geometry?.coordinates}
        emptyText="No time"
        footnote={footnote}
      />

      {motionCount > 0 && (
        <Box sx={tileRow(motionCount)}>
          {sog != null && <KpiTile label="SOG" value={fixed1(sog)} unit="kn" />}
          {cog != null && <CourseTile label="COG" deg={cog} color={color} />}
          {showHeading && heading != null && <CourseTile label="HDG" deg={heading} color={color} />}
        </Box>
      )}

      {voyageTiles.length > 0 && (
        <Box sx={tileRow(2)}>
          {voyageTiles.map((t) => (
            <KpiTile key={t.label} {...t} />
          ))}
        </Box>
      )}

      {detailTiles.length > 0 && (
        <DetailsToggle>
          <Box sx={tileRow(2)}>
            {detailTiles.map((t) => (
              <KpiTile key={t.label} {...t} />
            ))}
          </Box>
        </DetailsToggle>
      )}
    </Box>
  )
}
